import { useState, type ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@clarix/ui';

export interface CodeConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  /** Label above the code input. */
  inputLabel?: string;
  placeholder?: string;
  confirmLabel?: string;
  /** Confirm button text while `loading`. */
  loadingLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'destructive';
  /** Disables the form and flips the confirm label while the action runs. */
  loading?: boolean;
  /** Server/validation error surfaced under the input. */
  error?: string | null;
  /** Minimum code length before confirm is enabled (default 6). */
  minLength?: number;
  onConfirm: (code: string) => void | Promise<void>;
}

/**
 * Reusable "type a code to confirm this action" modal — e.g. entering an
 * authenticator/verification code to disable 2FA or authorise a sensitive
 * change. The sibling of ConfirmDialog for actions that need a code, not just a
 * yes/no. The parent owns the mutation: it passes `loading`/`error` and clears
 * `open` on success. Built on the shared `Dialog` primitive, following the
 * canonical Header → Body → Footer layout.
 *
 * The whole form lives in a child that only mounts while the dialog is open, so
 * the input resets automatically every time the dialog is reopened.
 */
export function CodeConfirmDialog({
  open,
  onOpenChange,
  loading = false,
  ...rest
}: CodeConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Don't let a click-away/ESC close the dialog mid-request.
        if (loading && !next) return;
        onOpenChange(next);
      }}
    >
      {/*
        `max-w-[calc(100%-2rem)]` is the MOBILE gutter.

        `DialogContent` is `w-full max-w-lg` centred by transform, so on a phone
        it spanned the full viewport with no margin — the panel met both screen
        edges and its 24px padding left the code input and the two footer buttons
        fighting for what was left. Capping the width below 100% gives it a 16px
        gutter each side; `sm:max-w-md` is unchanged, so nothing moves on a
        desktop.
      */}
      <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-md">
        <CodeConfirmForm loading={loading} {...rest} />
      </DialogContent>
    </Dialog>
  );
}

function CodeConfirmForm({
  title,
  description,
  inputLabel = 'Authentication code',
  placeholder = '123456',
  confirmLabel = 'Confirm',
  loadingLabel = 'Working…',
  cancelLabel = 'Cancel',
  variant = 'default',
  loading = false,
  error = null,
  minLength = 6,
  onConfirm,
}: Omit<CodeConfirmDialogProps, 'open' | 'onOpenChange'>) {
  const [code, setCode] = useState('');
  const trimmed = code.trim();

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || trimmed.length < minLength) return;
    void onConfirm(trimmed);
  };

  return (
    <form onSubmit={submit} className="flex flex-1 flex-col">
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        {description && <DialogDescription>{description}</DialogDescription>}
      </DialogHeader>
      <DialogBody className="space-y-2">
        <Label htmlFor="code-confirm-input">{inputLabel}</Label>
        <Input
          id="code-confirm-input"
          autoComplete="one-time-code"
          inputMode="numeric"
          placeholder={placeholder}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={loading}
        />
        {error && <p className="text-destructive text-xl">{error}</p>}
      </DialogBody>
      <DialogFooter>
        <DialogClose asChild>
          <Button type="button" variant="outline" disabled={loading}>
            {cancelLabel}
          </Button>
        </DialogClose>
        <Button type="submit" variant={variant} disabled={loading || trimmed.length < minLength}>
          {loading ? loadingLabel : confirmLabel}
        </Button>
      </DialogFooter>
    </form>
  );
}
